import { useDocumentTitle } from "@uidotdev/usehooks";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import Button from "@/components/Button";
import FormField from "@/components/FormField";
import FormFields from "@/components/FormFields";
import Header from "@/components/Header";
import { Input } from "@/components/Input";
import Label from "@/components/Label";
import Loader from "@/components/Loader";
import { useNotification } from "@/components/Notification";
import { ErrorMessages } from "@/constants/messages";
import { PageRoute, PageTitle } from "@/constants/pages";
import { useForgotEmailMutation } from "@/hooks/user";

const FormSchema = z.object({
  phone: z.string().min(10, {
    message: "10 digit phone number is required",
  }),
});

type FormSchema = z.infer<typeof FormSchema>;

export function ForgotEmailPage() {
  const notify = useNotification();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormSchema>({});
  const forgotEmailMutation = useForgotEmailMutation();
  const [showSuccess, setShowSuccess] = useState(false);

  function onGoToSignIn() {
    navigate(PageRoute.AUTH, { replace: true });
  }

  const onSubmit = handleSubmit((data) => {
    forgotEmailMutation.mutate(data.phone, {
      onSuccess: (result) => {
        if (result.success) {
          setShowSuccess(true);
        } else {
          notify(ErrorMessages.GENERIC, "error");
        }
      },
    });
  });

  useDocumentTitle(PageTitle.AUTH_FORGOT_EMAIL);

  if (showSuccess) {
    return (
      <div className={"flex flex-col"}>
        <Header className="mb-5">Forgot Your Email?</Header>

        <div className={"flex flex-col gap-4"}>
          <span className={"font-sans"}>
            A recovery text will be sent if there&apos;s an account associated
            with the phone number provided
          </span>

          <Button onClick={onGoToSignIn}>Sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mb-8">
      <Header className="mb-5">Forgot Your Email?</Header>

      <FormFields>
        <FormField>
          <Label htmlFor="phone">Please provide your phone number</Label>
          <Input
            id="phone"
            type="phone"
            autoComplete="phone"
            {...register("phone")}
            disabled={forgotEmailMutation.isPending}
          />
        </FormField>
        <span className={"text-center font-sans"}>
          A recovery text will be sent to the phone number provided
        </span>
        {forgotEmailMutation.isPending ? (
          <Loader />
        ) : (
          <Button type="submit" disabled={!isValid}>
            Text Recovery Details
          </Button>
        )}
      </FormFields>
    </form>
  );
}
